import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
    Button,
    FormControl,
    FormControlLabel,
    InputLabel,
    Input,
    Switch,
    Tooltip, Zoom
} from '@mui/material';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';

import FormLabel from '@mui/material/FormLabel';
import { withTranslation } from 'react-i18next';
import { fetchLicenses } from '../../../actions';
import {
    mapLicenseFiltersStateToQuery,
    dateFormatValidation,
    LICENSING_TYPE,
    checkSwitchDisableStatus,
    LICENSES_TYPES_ID,
    LOCATION_STATES_ID,
    LOCATION_STATES,
    getMinDate,
    getNextYearDateFormatWithoutTime
} from '../../../utils';
import { updateFilters, applyFilters, resetApplyFilters } from '../../../actions/licensesFilters';
import { LicenseIncorrectDataTypeahead, LicenseTypesTypeahead, UsersMultiTypeahead } from '../Typeahead';
import LicensesVmMultiTypeahead from '../Typeahead/LicensesVmMultiTypeahead';
import LicenseManufacturerTypeahead from '../Typeahead/LicenseManufacturerTypeahead';
import ProductTitleTypeahead from '../Typeahead/ProductTitleTypeahead';
import LocationTreeModeTypeahead from '../Typeahead/LocationTreeModeTypeahead';
import ProductTitleMultiInput from '../Typeahead/ProductTitleMultiInput';
import LicenseIdMultiInput from '../Typeahead/LicenseIdMultiInput';
import LicensesIuMultiTypeahead from '../Typeahead/LicensesIuMultiTypeahead';
import ProjectsTypeahead from '../Typeahead/ProjectsTypeahead';
import DateRangePicker from '../DateRangePicker';
import GeneralProgressBar from '../GeneralProgressBar';

const styles = theme => ({
    button: {
        margin: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        flexGrow: 1
    },
    filterForm: {
        padding: theme.spacing(1)
    },
    controlButtons: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around'
    },
    deleteIcon: {
        position: 'absolute',
        right: 0,
        top: '40%'
    },
    strictContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    switchForm: {
        paddingLeft: 5,
        '& label': {
            marginRight: 5,
            marginLeft: 0,
            flexDirection: 'column',
            justifyContent: 'flex-end'
        }
    }
});

const minDate = getMinDate('01/01/1996');
const maxDate = getNextYearDateFormatWithoutTime();

class LicenseFilter extends Component {
    constructor(props) {
        super(props);

        this.state = {
            deliveryDateFocusedInput: null,
            expirationDateFocusedInput: null,
            isSearchByFullTitle: false,
            productTitleValue: '',
            licenseIdValue: '',
            errors: {
                deliveryDateFrom: null,
                deliveryDateTo: null,
                expirationDateFrom: null,
                expirationDateTo: null,
            },
            licensingTypesSwitch: {
                perpetual: false,
                subscription: false
            },
        };
    }

    componentDidMount() {
        dateFormatValidation('deliveryDateFrom', 'deliveryDateTo');
    }

    handleResetClick = async () => {
        await this.props.resetApplyFilters();

        const filters = mapLicenseFiltersStateToQuery(this.props.licensesFilters);
        this.setState({
            isSearchByFullTitle: false,
            licensingTypesSwitch: {
                perpetual: false,
                subscription: false
            }
        });
        this.props.fetchLicenses(filters);
    };

    handleClickApply = () => {
        const { licensesFilters } = this.props;

        const { expirationDateFrom, expirationDateTo, deliveryDateFrom, deliveryDateTo } = this.state.errors;

        const isError = expirationDateFrom || expirationDateTo || deliveryDateFrom || deliveryDateTo;

        if (isError) return;

        if (this.state.productTitleValue) {
            this.handleAddNameMultiInput(this.state.productTitleValue);
        }
        const filters = !this.state.productTitleValue
            ? licensesFilters
            : { ...licensesFilters,
                licenseNameParts: [...licensesFilters.licenseNameParts, { value: this.state.productTitleValue }] };

        const appliedFilters = mapLicenseFiltersStateToQuery(filters);

        this.props.applyFilters(filters);
        this.props.fetchLicenses(appliedFilters);
    };

    handleChange = e => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            [e.target.name]: e.target.value
        });
    };

    handleChangeProductTitleValue = value => {
        this.setState({ productTitleValue: value });
    }

    handleChangeLicenseIdValue = value => {
        this.setState({ licenseIdValue: value });
    }

    handleChangeOrdered = e => {
        e.persist();
        const { name, checked } = e.target;
        this.handleChange({ target: { name, value: checked || null } });
    };

    handleChangeSubscriptionOrPerpetual = (e, name) => {
        this.setState({
            licensingTypesSwitch: {
                [name]: e.target.checked
            }
        });

        const value = e.target.checked ? name : LICENSING_TYPE.allLicenses;

        this.handleChange({ target: { name: 'licensingType', value } });
    };

    handleChangeStatusOffices = name => e => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            [name]: e.target.checked ? LOCATION_STATES.all : LOCATION_STATES.onlyActive
        });
    };

    handleChangeFreeLicenses = (e, name) => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            freeLicense: {
                ...this.props.licensesFilters.freeLicense,
                [name]: e.target.checked
            },
        });
    };

    handleChangeMultiSuggestion = name => suggestions => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            [name]: suggestions
        });
    };

    handleChangeMultiLicenseType = name => suggestions => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            [name]: suggestions,
            licenseType: suggestions?.id
        });
    };

    handleChangeDate = name => date => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            [name]: date,
        });
    };

    handleChangeSearchMethod = e => {
        this.setState({ isSearchByFullTitle: e.target.checked });
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseNameParts: [],
            licenseTitles: []
        });
    }

    handleAddNameMultiInput = value => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseNameParts: [...this.props.licensesFilters.licenseNameParts, { value }],
        });
        this.setState({ productTitleValue: '' });
    }

    handleAddIdMultiInput = value => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseIds: [...this.props.licensesFilters.licenseIds, { value }],
        });
        this.setState({ licenseIdValue: '' });
    }

    handleDeleteItemMultiInput = values => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseNameParts: values,
        });
    }

    handleDeleteItemIdMultiInput = values => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseIds: values,
        });
    }

    handleDelete = () => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseVerifyKind: ''
        });
    }

    handleChangeLicenseTypeStrict = e => {
        this.props.updateFilters({
            ...this.props.licensesFilters,
            licenseTypeStrict: e.target.checked
        });
    }

    handleErrorDate = (error, name) => {
        this.setState(state => ({
            ...state,
            errors: {
                ...state.errors,
                [name]: error
            }
        }));
    }

    render() {
        const { classes, licensesFilters, offices, t } = this.props;
        const { freeLicense, officeState, resourceOwnerState } = licensesFilters;
        const { yes, no } = freeLicense;
        const {
            isSearchByFullTitle,
            productTitleValue,
            licenseIdValue,
            licensingTypesSwitch,
            errors
        } = this.state;

        const isLicenseTypeUser = licensesFilters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.user);
        const isLicenseTypeVM = licensesFilters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.vm);
        const isLicenseTypeIU =
            licensesFilters.licenseTypes?.some(el => el.id === LICENSES_TYPES_ID.infrastructureUnit);

        const isOfficeState = officeState === LOCATION_STATES_ID.all.id;
        const isResourceOwnerState = resourceOwnerState === LOCATION_STATES_ID.all.id;

        return offices ? (
            <div
                className={classNames('filter', classes.filterForm)}
            >

                <FormControl fullWidth>
                    <InputLabel
                        htmlFor="searchData"
                        variant="standard"
                    >
                        {t('search')}
                    </InputLabel>
                    <Input
                        id="searchData"
                        name="searchData"
                        value={licensesFilters.searchData}
                        onChange={this.handleChange}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <LicenseManufacturerTypeahead
                        margin="none"
                        name="licenseManufacturers"
                        value={licensesFilters.licenseManufacturers}
                        labelId="manufacturer"
                        onChangeSuggestion={this.handleChangeMultiSuggestion('licenseManufacturers')}
                        t={t}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <LicenseIdMultiInput
                        value={licenseIdValue}
                        onChangeValue={this.handleChangeLicenseIdValue}
                        values={licensesFilters.licenseIds}
                        onChangeMultiInput={this.handleAddIdMultiInput}
                        onDeleteValue={this.handleDeleteItemIdMultiInput}
                        t={t}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    {
                        isSearchByFullTitle
                            ? (
                                <ProductTitleMultiInput
                                    value={productTitleValue}
                                    onChangeValue={this.handleChangeProductTitleValue}
                                    values={licensesFilters.licenseNameParts}
                                    onChangeMultiInput={this.handleAddNameMultiInput}
                                    onDeleteValue={this.handleDeleteItemMultiInput}
                                    t={t}
                                />
                            ) : (
                                <ProductTitleTypeahead
                                    margin="none"
                                    value={licensesFilters.licenseTitles}
                                    labelId="product"
                                    name="productTitles"
                                    licenseManufacturers={licensesFilters.licenseManufacturerIds}
                                    onChangeSuggestion={this.handleChangeMultiSuggestion('licenseTitles')}
                                    t={t}
                                />
                            )
                    }

                </FormControl>

                <FormControl>
                    <FormControlLabel
                        control={(
                            <Switch
                                onChange={this.handleChangeSearchMethod}
                                checked={isSearchByFullTitle}
                            />
                        )}
                        label={t('searchMethod')}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                    className={classes.strictContainer}
                >
                    <LicenseTypesTypeahead
                        margin="none"
                        name="licenseTypes"
                        value={licensesFilters.licenseTypes}
                        labelId="licenseTypes"
                        onChangeSuggestion={this.handleChangeMultiLicenseType('licenseTypes')}
                        t={t}
                    />
                    <FormControl className={this.props.classes.switchForm}>
                        <FormControlLabel
                            control={(
                                <Switch
                                    onChange={this.handleChangeLicenseTypeStrict}
                                    checked={licensesFilters.licenseTypeStrict}
                                />
                            )}
                            label={t('strict')}
                            labelPlacement="bottom"
                        />
                    </FormControl>
                </FormControl>

                {(isLicenseTypeUser ||
                    isLicenseTypeVM ||
                    isLicenseTypeIU) && (
                    <>
                        {isLicenseTypeUser && (
                            <Tooltip
                                title={t('addInfoUser')}
                                aria-label="Product title"
                                arrow
                                enterDelay={300}
                                TransitionComponent={Zoom}
                                placement="right"
                            >
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                >
                                    <UsersMultiTypeahead
                                        margin="none"
                                        value={licensesFilters.users}
                                        labelId="user"
                                        onChangeSuggestion={this.handleChangeMultiSuggestion('users')}
                                        animatedPlaceholder
                                        t={t}
                                    />
                                </FormControl>
                            </Tooltip>

                        )}
                        {isLicenseTypeVM && (
                            <Tooltip
                                title={t('addInfoVirtualMachine')}
                                aria-label="Product title"
                                arrow
                                enterDelay={300}
                                TransitionComponent={Zoom}
                                placement="right"
                            >
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                >
                                    <LicensesVmMultiTypeahead
                                        margin="none"
                                        value={licensesFilters.devices}
                                        onChangeSuggestion={this.handleChangeMultiSuggestion('devices')}
                                        t={t}
                                    />
                                </FormControl>
                            </Tooltip>
                        )}
                        {isLicenseTypeIU && (
                            <Tooltip
                                title={t('addInfoInfrastructureUnit')}
                                aria-label="Product title"
                                arrow
                                enterDelay={300}
                                TransitionComponent={Zoom}
                                placement="right"
                            >
                                <FormControl
                                    fullWidth
                                    margin="dense"
                                >
                                    <LicensesIuMultiTypeahead
                                        value={licensesFilters.infrastructureUnit}
                                        onChangeSuggestion={this.handleChangeMultiSuggestion('infrastructureUnit')}
                                        t={t}
                                    />
                                </FormControl>
                            </Tooltip>
                        )}
                    </>

                )}

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isOfficeState}
                                onChange={this.handleChangeStatusOffices('officeState')}
                            />
                        )}
                        label={t('active/All')}
                    />
                    <LocationTreeModeTypeahead
                        data={[isOfficeState ? offices.all : offices.onlyActive]}
                        placeholder="office"
                        locationId={licensesFilters.offices.map(location => location.value)}
                        strict={licensesFilters.strict}
                        setLocation={value =>
                            this.handleChangeMultiSuggestion('offices')(value.map(item => ({ value: item })))}
                        setStrict={value =>
                            this.handleChangeMultiSuggestion('strict')(value)}
                        hierarchical
                        multi
                        descendants
                        isLicense
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={isResourceOwnerState}
                                onChange={this.handleChangeStatusOffices('resourceOwnerState')}
                            />
                        )}
                        label={t('active/All')}
                    />
                    <LocationTreeModeTypeahead
                        data={[isResourceOwnerState ? offices.all : offices.onlyActive]}
                        placeholder="resourceOwner"
                        locationId={licensesFilters.resourceOwner.map(location => location.value)}
                        strict={licensesFilters.resourceOwnerStrict}
                        setLocation={value =>
                            this.handleChangeMultiSuggestion('resourceOwner')(value.map(item => ({ value: item })))}
                        setStrict={value =>
                            this.handleChangeMultiSuggestion('resourceOwnerStrict')(value)}
                        hierarchical
                        multi
                        descendants
                        isLicense
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <ProjectsTypeahead
                        animatedPlaceholder
                        labelId="project"
                        margin="none"
                        name="project"
                        value={licensesFilters.projects}
                        isMulti
                        onChangeSuggestion={this.handleChangeMultiSuggestion('projects')}
                        t={t}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={!!licensingTypesSwitch.subscription}
                                onChange={e => this.handleChangeSubscriptionOrPerpetual(e, 'subscription')}
                            />
                        )}
                        label={t('subscription')}
                    />
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={!!licensingTypesSwitch.perpetual}
                                onChange={e => this.handleChangeSubscriptionOrPerpetual(e, 'perpetual')}
                            />
                        )}
                        label={t('perpetual')}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <FormLabel component="legend">
                        {t('freeLicense')}
                    </FormLabel>
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={yes}
                                onChange={e => this.handleChangeFreeLicenses(e, 'yes')}
                                disabled={checkSwitchDisableStatus(yes, freeLicense)}
                            />
                        )}
                        label={t('yes')}
                    />
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={no}
                                onChange={e => this.handleChangeFreeLicenses(e, 'no')}
                                disabled={checkSwitchDisableStatus(no, freeLicense)}
                            />
                        )}
                        label={t('no')}
                    />
                </FormControl>

                <DateRangePicker
                    isColumn
                    startName="deliveryDateFrom"
                    endName="deliveryDateTo"
                    title="deliveryDate"
                    minDate={minDate}
                    maxDate={maxDate}
                    value={{
                        startDate: licensesFilters.deliveryDateFrom,
                        endDate: licensesFilters.deliveryDateTo
                    }}
                    handleChangeStartDate={this.handleChangeDate}
                    handleChangeEndDate={this.handleChangeDate}
                    handleError={this.handleErrorDate}
                    errors={errors}
                    t={t}
                />

                {licensesFilters.licensingType !== LICENSING_TYPE.perpetual && (
                    <DateRangePicker
                        isColumn
                        startName="expirationDateFrom"
                        endName="expirationDateTo"
                        title="expirationDate"
                        minDate={minDate}
                        maxDate={maxDate}
                        value={{
                            startDate: licensesFilters.expirationDateFrom,
                            endDate: licensesFilters.expirationDateTo
                        }}
                        handleChangeStartDate={this.handleChangeDate}
                        handleChangeEndDate={this.handleChangeDate}
                        handleError={this.handleErrorDate}
                        errors={errors}
                        t={t}
                    />
                )}

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <FormControlLabel
                        control={(
                            <Switch
                                name="isOrdered"
                                onChange={this.handleChangeOrdered}
                                checked={licensesFilters.isOrdered}
                            />
                        )}
                        label={t('orderedOnly')}
                    />
                </FormControl>

                <FormControl
                    fullWidth
                    margin="dense"
                >
                    <LicenseIncorrectDataTypeahead
                        margin="none"
                        name="licenseVerifyKind"
                        value={licensesFilters.licenseVerifyKind}
                        labelId="licenseVerifyKind"
                        onChangeSuggestion={this.handleChangeMultiSuggestion('licenseVerifyKind')}
                        t={t}
                    />
                </FormControl>

                <div className={classes.controlButtons}>
                    <Button
                        variant="contained"
                        className={classes.button}
                        onClick={this.handleResetClick}
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.button}
                        color="secondary"
                        type="submit"
                        onClick={this.handleClickApply}
                    >
                        {t('apply')}
                    </Button>
                </div>
            </div>
        ) : (<GeneralProgressBar />);
    }
}

LicenseFilter.propTypes = {
    classes: PropTypes.shape({
        controlButtons: PropTypes.string,
        button: PropTypes.string,
        filterForm: PropTypes.string,
    }),
    licensesFilters: PropTypes.shape({
        offices: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.number,
            }),
        ),
        strict: PropTypes.bool,
        officeState: PropTypes.number,
        resourceOwnerState: PropTypes.number,
        isOrdered: PropTypes.bool,
        licenseManufacturers: PropTypes.array,
        licenseManufacturerIds: PropTypes.array,
        licenseType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        licensingType: PropTypes.string,
        users: PropTypes.array,
        devices: PropTypes.array,
        licenseTitles: PropTypes.array,
        deliveryDateFrom: PropTypes.object,
        deliveryDateTo: PropTypes.object,
    }),
    offices: PropTypes.object,
    resetApplyFilters: PropTypes.func,
    fetchLicenses: PropTypes.func,
    applyFilters: PropTypes.func,
    updateFilters: PropTypes.func,
};

const mapStateToProps = ({ licensesFilters, infrastructure }) => ({
    licensesFilters: licensesFilters.currentFilters,
    offices: infrastructure.locations[0]
});

export default compose(
    withTranslation('licenses'),
    connect(mapStateToProps, { fetchLicenses, updateFilters, applyFilters, resetApplyFilters }),
    withStyles(styles)
)(LicenseFilter);
